import { create } from '@bufbuild/protobuf'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type SplitSupplyNoticeKey,
  SplitSupplyNoticeKeySchema,
  SplitSupplyNoticeSection,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'

import {
  type CivilDate,
  civilDateToInputValue,
  civilDateToProtoDate,
  parseDateInputValue,
  protoDateToCivilDate,
} from '~/models/civilDate'

/**
 * 分割供給の 1 通 (電文 1 通にまとめる単位) の識別と、 詳細画面の URL との相互変換。
 *
 * 通は計画作成の時に、 自然キー (断面 × 対象日 × エリア × 自社の事業者 × 負荷追随分供給者 ×
 * 負荷追随分の契約識別番号 1) で保存され、 id を持たない。 作成後にマスタを変えてもキーは変わらない。
 * 詳細画面は同じ値を query に持つ。
 */

const SECTION_PARAM: Record<SplitSupplyNoticeSection, string | undefined> = {
  [SplitSupplyNoticeSection.UNSPECIFIED]: undefined,
  [SplitSupplyNoticeSection.DAY_AHEAD]: 'day',
  [SplitSupplyNoticeSection.MONTHLY]: 'month',
}

const PARAM_NAMES = {
  section: 'section',
  target: 'target',
  area: 'area',
  own: 'own',
  loadFollowing: 'lf',
  contractId: 'contract',
  deliveryDate: 'day',
} as const

/** 一覧の行 (React の key) に使う、 通を一意に表す文字列。 */
export function noticeRowKey(key: SplitSupplyNoticeKey): string {
  const target = protoDateToCivilDate(key.targetDate)
  return [
    key.section,
    target === undefined ? '' : civilDateToInputValue(target),
    key.areaCode,
    key.ownBusinessMasterId,
    key.loadFollowingBusinessMasterId,
    key.loadFollowingContractId,
  ].join('|')
}

/** 詳細画面への URL の query (`?` を含まない)。 月間は表示する日 (deliveryDate) も持つ。 */
export function noticeDetailSearch(
  key: SplitSupplyNoticeKey,
  deliveryDate: CivilDate,
): string {
  const target = protoDateToCivilDate(key.targetDate)
  const section = SECTION_PARAM[key.section]
  if (target === undefined || section === undefined) {
    throw new Error('notice key without a section or a target date')
  }
  const params = new URLSearchParams()
  params.set(PARAM_NAMES.section, section)
  params.set(PARAM_NAMES.target, civilDateToInputValue(target))
  params.set(PARAM_NAMES.area, String(key.areaCode))
  params.set(PARAM_NAMES.own, key.ownBusinessMasterId)
  params.set(PARAM_NAMES.loadFollowing, key.loadFollowingBusinessMasterId)
  params.set(PARAM_NAMES.contractId, key.loadFollowingContractId)
  params.set(PARAM_NAMES.deliveryDate, civilDateToInputValue(deliveryDate))
  return params.toString()
}

export type NoticeLocation = {
  key: SplitSupplyNoticeKey
  deliveryDate: CivilDate
}

const sectionFromParam = (
  raw: string | null,
): SplitSupplyNoticeSection | undefined => {
  if (raw === 'day') return SplitSupplyNoticeSection.DAY_AHEAD
  if (raw === 'month') return SplitSupplyNoticeSection.MONTHLY
  return undefined
}

const areaFromParam = (raw: string | null): AreaCode | undefined => {
  if (raw === null || !/^\d+$/.test(raw)) return undefined
  const code = Number(raw) as AreaCode
  return code in AreaCode && code !== AreaCode.UNSPECIFIED ? code : undefined
}

const dateFromParam = (raw: string | null): CivilDate | undefined =>
  raw === null || !/^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? undefined
    : parseDateInputValue(raw)

/**
 * 詳細画面の query から通の識別と表示する日を読む。 欠けた・壊れた query は undefined
 * (呼び出し側は「通が見つからない」を案内する)。
 */
export function parseNoticeDetailSearch(
  search: URLSearchParams,
): NoticeLocation | undefined {
  const section = sectionFromParam(search.get(PARAM_NAMES.section))
  const target = dateFromParam(search.get(PARAM_NAMES.target))
  const area = areaFromParam(search.get(PARAM_NAMES.area))
  const own = search.get(PARAM_NAMES.own)
  const loadFollowing = search.get(PARAM_NAMES.loadFollowing)
  const contractId = search.get(PARAM_NAMES.contractId)
  const deliveryDate = dateFromParam(search.get(PARAM_NAMES.deliveryDate))
  if (
    section === undefined ||
    target === undefined ||
    area === undefined ||
    own === null ||
    own === '' ||
    loadFollowing === null ||
    loadFollowing === '' ||
    contractId === null ||
    contractId === '' ||
    deliveryDate === undefined
  ) {
    return undefined
  }
  return {
    key: create(SplitSupplyNoticeKeySchema, {
      section,
      targetDate: civilDateToProtoDate(target),
      areaCode: area,
      ownBusinessMasterId: own,
      loadFollowingBusinessMasterId: loadFollowing,
      loadFollowingContractId: contractId,
    }),
    deliveryDate,
  }
}
